/**
 * Detailansicht einer Ausschreibung als Seitenpanel rechts neben dem Board (ersetzt die lange
 * Aufklappung in der Tabelle).
 *
 * Das Board bleibt dahinter sichtbar und unverändert — Filter, Auswahl und Scrollposition gehören
 * ihm, das Panel liegt nur darüber. Was das Panel ändert, schreibt es als **eine Zeile** zurück
 * (`onRowChanged`, aus `GET …/tender-board/:tenderId`), statt das Board neu zu laden.
 *
 * Aufbau (Wireframe 19.10.2026): ein fester Kopf (Blättern durch die gefilterte Liste, Titel,
 * Bestandskunden-Badge, drei Kacheln), darunter der Block **„Offene Aufgabe“** und vier Reiter.
 *
 * „Offene Aufgabe“ ist generisch für **jede** Human-in-the-Loop-Aufgabe des Prozesses, nicht nur für
 * die Triage: die Frage ist der `userPrompt` der Aufgabe, die Knöpfe folgen ihrem `uxElement`
 * (Ja/Nein, Bestätigen, Freitext) und senden genau die Antwort, die auch der Chat sendet. Was das Panel
 * nicht selbst beantworten kann (Auswahl, Formular, Checkliste), führt in den Chat-Reiter.
 *
 * Nicht gezeigt wird, was der Server nicht liefert: kein „Schritt 3 von 5“ (es gibt keine
 * Schrittliste), kein Korrigieren am Ausführungsort (kein Endpunkt). Siehe die Ticket-Vorschläge im PR.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { ArrowRight, Check, ChevronLeft, ChevronRight, Loader2, Upload, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { userAPI } from "../../UserAPI";
import { TaskCompletionInterruptedError } from "../../general/Errors";
import { authenticationAtom } from "../../State";
import {
  useTenderSearchProfileAPI,
  type TenderActivityEntry,
  type TenderBoardRow,
  type TenderLocationResult,
  type TenderPanelConcept,
  type TenderPanelFacts,
} from "../../TenderSearchProfileAPI";
import type { BoardDialog } from "./TenderBoardActions";
import { ExistingCustomerBadge } from "./ExistingCustomerBadge";
import { FitLight } from "./FitLight";
import { describeDeadline } from "./tenderDeadline";
import { isDateOnly, isNetworkError, plainExcerpt, relativeTime, taskExcerptSource } from "./tenderPanelText";
import { TenderPanelOverview } from "./TenderPanelOverview";
import { TenderPanelDocuments } from "./TenderPanelDocuments";
import { TenderPanelChat } from "./TenderPanelChat";
import { TenderPanelHistory } from "./TenderPanelHistory";
import { ConceptDocumentActions, TenderOutlineReview, TenderPanelConceptTab } from "./TenderPanelConcept";
import { phaseBarSteps } from "./tenderPhaseBar";

type PanelTab = "overview" | "documents" | "concept" | "chat" | "history";

const TERMINAL_PROCESS = new Set(["COMPLETED", "TERMINATED", "FAILED"]);
/** How often an open panel looks for the next task while the process works on its own. */
const POLL_MS = 15_000;
/** Nach einer Antwort öfter: die nächste Aufgabe soll erscheinen, sobald es sie gibt. */
const ANSWERED_POLL_MS = 5_000;
/** Wie oft und in welchem Abstand die Worklist nachgeladen wird, bis die Aufgabe der Zeile darin steht. */
const WORKLIST_RETRIES = 5;
const WORKLIST_RETRY_MS = 2_000;

/** Answer kinds the panel can give itself — everything else is answered in the chat. */
type AnswerMode = "triage-start" | "slider" | "confirmation" | "text" | "chat" | "none";

const answerModeOf = (row: TenderBoardRow): AnswerMode => {
  if (row.triageState === "dismissed") return "none";
  if (!row.process) return "triage-start";
  const ux = row.currentStep?.uxElement ?? null;
  if (!row.currentStep) return "none";
  if (ux === "slider") return "slider";
  if (ux === "confirmation") return "confirmation";
  if (ux === "text-input" || !ux) return "text";
  return "chat";
};

/**
 * Der Entwurf im Antwortfeld, je Aufgabe im sessionStorage. Im Komponentenzustand allein ging er
 * verloren, sobald irgendetwas das Panel aushängt (Prod 22.10.2026: getippte Korrektur weg, im
 * selben Moment verschwand der Bereich „Ausschreibungen" aus der Seitenleiste). Bequemlichkeit:
 * ohne Speicher gilt das Feld wie bisher nur, solange das Panel steht.
 */
const draftKey = (taskId: string) => `tri.tenderPanel.draft.${taskId}`;
const readDraft = (taskId: string): string => {
  try {
    return window.sessionStorage.getItem(draftKey(taskId)) ?? "";
  } catch {
    return "";
  }
};
const writeDraft = (taskId: string, text: string) => {
  try {
    if (text) window.sessionStorage.setItem(draftKey(taskId), text);
    else window.sessionStorage.removeItem(draftKey(taskId));
  } catch {
    /* Ohne Speicher bleibt der Entwurf nur im Zustand. */
  }
};

const initialsOf = (name: string): string =>
  name
    .split(/[\s.\-_+@]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

export interface TenderDetailPanelProps {
  tenderId: string | null;
  /** The row as the board has it; null when the tender is not in the current list (a link). */
  row: TenderBoardRow | null;
  /** Position in the board's filtered list, for „2 von 23 in deiner Liste“. */
  position: { index: number; total: number } | null;
  onPrevious?: () => void;
  onNext?: () => void;
  onClose: () => void;
  openInNewTab: boolean;
  onOpenInNewTabChange: (value: boolean) => void;
  currentUser: string | null;
  people: Record<string, string>;
  /** Deployed workflows a triage tender can be started on. */
  definitions: { key: string; text: string }[];
  /** Write one changed row back into the board — no reload of the list. */
  onRowChanged: (row: TenderBoardRow) => void;
  /** The board's own dialogs (Verwerfen, Weitergeben, Prozess wählen). */
  onOpenDialog: (dialog: BoardDialog) => void;
  /** Bumped by the board after one of its dialogs changed this tender. */
  refreshSignal?: number;
}

/** A popover, select or menu inside the panel — Radix renders it in a portal, outside the sheet. */
const POPPER = "[data-radix-popper-content-wrapper]";

export function TenderDetailPanel(props: TenderDetailPanelProps) {
  const { tenderId, onClose } = props;
  return (
    <Sheet open={!!tenderId} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        hideClose
        // The sheet and the popovers inside it ship different copies of Radix' dismissable layer
        // (1.1.19 vs 1.1.11), so they do not know of each other: Escape in the Bestandskunden popover,
        // or a click on its „Ja, ist Bestandskunde“, read to the sheet as "close". While a popper is
        // open it gets the key and the click, not the sheet.
        onEscapeKeyDown={(event) => {
          if (document.querySelector(POPPER)) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (event.target instanceof Element && event.target.closest(POPPER)) event.preventDefault();
        }}
        // ~48 % of the window; full width on a phone. `max-w-none` lifts the sheet's own 24rem cap.
        className="flex w-full flex-col gap-0 p-0 sm:max-w-none md:w-[48vw] md:min-w-[640px]"
        data-testid="tender-detail-panel"
        aria-describedby={undefined}
      >
        {tenderId && <PanelBody key={tenderId} {...props} tenderId={tenderId} />}
      </SheetContent>
    </Sheet>
  );
}

function PanelBody({
  tenderId,
  row: listRow,
  position,
  onPrevious,
  onNext,
  onClose,
  openInNewTab,
  onOpenInNewTabChange,
  currentUser,
  people,
  definitions,
  onRowChanged,
  onOpenDialog,
  refreshSignal = 0,
}: TenderDetailPanelProps & { tenderId: string }) {
  const { t } = useTranslation();
  const [authentication] = useAtom(authenticationAtom) as any;
  const api = useTenderSearchProfileAPI();
  /** Fehlertext für den Menschen — ein Netzfehler nie roh („Failed to fetch"). */
  const errorText = (err: any): string =>
    isNetworkError(err) ? t("tenderBoard.panel.task.networkError") : (err?.message ?? String(err));
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const [loadedRow, setLoadedRow] = useState<TenderBoardRow | null>(null);
  const [facts, setFacts] = useState<TenderPanelFacts | null>(null);
  const [concept, setConcept] = useState<TenderPanelConcept | null>(null);
  const [panelError, setPanelError] = useState<string | null>(null);
  const [entries, setEntries] = useState<TenderActivityEntry[] | undefined>(undefined);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [location, setLocation] = useState<TenderLocationResult | null | undefined>(undefined);
  const [conversation, setConversation] = useState<any | null>(null);
  const [conversationError, setConversationError] = useState<string | null>(null);
  const [run, setRun] = useState<{ running: boolean; since?: string } | null>(null);
  const [tab, setTab] = useState<PanelTab>("overview");
  const [highlightQuestion, setHighlightQuestion] = useState(0);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [answerNote, setAnswerNote] = useState<string | null>(null);
  /**
   * Die Aufgabe, auf die gerade geantwortet wurde. `…/tasks/:task/complete` kehrt erst zurück, wenn
   * der Prozess die nächste Aufgabe angelegt hat — nach „Passt so" also erst, wenn das Konzept
   * geschrieben ist (gut zwei Minuten). So lange zeigte die Karte die beantwortete Frage, ihre
   * Knöpfe und den alten Assistententext, darunter „wird übermittelt" (Prod-Test 24.10.2026).
   */
  const [answered, setAnswered] = useState<{ taskId: string; label: string } | null>(null);
  const [freeText, setFreeText] = useState("");

  // The list's copy is fresher after a board reload; the panel's own after a panel action.
  const row = loadedRow ?? listRow;
  const conversationId = row?.process?.conversationId ?? null;

  // Zeile, Verlauf und Konversation werden von mehreren Stellen zugleich nachgeladen (nach einer
  // Antwort, nach einer Aktion, im Polling). Kam eine ältere Antwort zuletzt an, überschrieb sie die
  // neuere — der Verlauf-Zähler sprang 53 → 49 → 53 (Prod 22.10.2026), und eine alte Zeile zeigte alte
  // Knöpfe. Es gilt nur die Antwort der jüngsten Anfrage, wie beim Board (`latestLoad`).
  const latest = useRef({ row: 0, activity: 0, conversation: 0 });

  const refresh = useCallback(async () => {
    const request = ++latest.current.row;
    try {
      const panel = await api.getTenderPanel(tenderId);
      if (!mounted.current || request !== latest.current.row) return;
      setLoadedRow(panel.row);
      setFacts(panel.facts);
      setConcept(panel.concept ?? null);
      setPanelError(null);
      onRowChanged(panel.row);
    } catch (err: any) {
      if (mounted.current && request === latest.current.row) setPanelError(errorText(err));
    }
    // `api` and `onRowChanged` are rebuilt on every render of their owners.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenderId]);

  const loadActivity = useCallback(async () => {
    const request = ++latest.current.activity;
    const current = () => mounted.current && request === latest.current.activity;
    setEntriesLoading(true);
    try {
      const response = await api.getTenderActivity(tenderId);
      if (current()) setEntries(response.entries);
    } catch {
      if (current()) setEntries([]);
    } finally {
      if (current()) setEntriesLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenderId]);

  useEffect(() => {
    refresh();
    loadActivity();
    api
      .getTenderLocation(tenderId)
      .then((result) => mounted.current && setLocation(result))
      .catch(() => mounted.current && setLocation(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenderId]);

  // A board dialog (Verwerfen, Weitergeben, Prozess) changed the list's row — the trail changed too.
  useEffect(() => {
    if (refreshSignal) {
      setLoadedRow(null);
      loadActivity();
    }
  }, [refreshSignal, loadActivity]);

  const loadConversation = useCallback(async () => {
    if (!conversationId) return;
    const request = ++latest.current.conversation;
    try {
      const loaded = await api.getProcessConversation(conversationId);
      if (!mounted.current || request !== latest.current.conversation) return;
      setConversation(loaded);
      setConversationError(null);
    } catch (err: any) {
      const raw = errorText(err);
      if (mounted.current && request === latest.current.conversation) setConversationError(/forbidden|403/i.test(raw) ? t("tenderBoard.conversation.noAccess") : raw);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  useEffect(() => {
    setConversation(null);
    loadConversation();
  }, [loadConversation]);

  const step = row?.currentStep ?? null;
  const draftTaskId = step?.taskId ?? null;
  /** The row still shows the task just answered — treat it as done until the next one arrives. */
  const pendingAnswer = !!answered && !!step?.taskId && answered.taskId === step.taskId;
  // Riss die Verbindung ab (das Konzept braucht länger als der Proxy wartet), stand „die Verbindung
  // ist abgerissen" auch dann noch unter der Karte, als die nächste Aufgabe längst da war (Nightly
  // 24.10.2026). Ist sie da, ist die Antwort angekommen — dann heißt es nur noch „übermittelt".
  const nextTaskArrived = !!answered && !!step?.taskId && answered.taskId !== step.taskId;
  /** Schon angekommen — ein später abreißender Request darf das nicht mehr als Abriss melden. */
  const arrived = useRef<string | null>(null);
  useEffect(() => {
    if (!nextTaskArrived || !answered) return;
    arrived.current = answered.taskId;
    setAnswerNote(t("tenderBoard.panel.task.sent", { answer: answered.label }));
    setAnswered(null);
  }, [nextTaskArrived, answered, t]);
  useEffect(() => {
    setFreeText(draftTaskId ? readDraft(draftTaskId) : "");
  }, [draftTaskId]);
  const changeFreeText = (text: string) => {
    setFreeText(text);
    if (draftTaskId) writeDraft(draftTaskId, text);
  };
  const processLive = !!row?.process && !TERMINAL_PROCESS.has(String(row.process.status ?? "").toUpperCase());

  // While the process works on its own, look for the next task — so „erscheint hier“ is true.
  useEffect(() => {
    if (!conversationId || !processLive) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const state = await userAPI.getConversationRun(authentication.customer, conversationId);
        if (!cancelled) setRun(state);
      } catch {
        /* The dot is best-effort. */
      }
    };
    tick();
    const timer = window.setInterval(() => {
      tick();
      // Ohne offene Aufgabe (oder mit einer schon beantworteten): nach der nächsten sehen, und die
      // Konversation mitladen — sonst fehlten im Chat-Reiter die Nachrichten des laufenden Schritts.
      if (!step || pendingAnswer) {
        refresh();
        loadConversation();
      }
    }, pendingAnswer ? ANSWERED_POLL_MS : POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, processLive, !!step, pendingAnswer]);

  /** The task as the server put it on the viewer's worklist — present only if they may answer. */
  const worklistTask = useMemo(() => {
    const list: any[] = conversation?.worklist ?? [];
    return list.find((task) => String(task?._id) === step?.taskId) ?? list[0] ?? null;
  }, [conversation, step?.taskId]);

  // Nach einem Rücksprung („Passt die Gliederung?" erneut) legt die Engine die neue Aufgabe erst
  // Sekunden nach dem Senden an — die Konversation samt Worklist war da schon geladen. Ohne
  // Nachladen blieben die Knöpfe gesperrt und „notYours" erschien fälschlich (Prod 22.10.2026).
  // Also: solange die Aufgabe der Zeile in der Worklist fehlt, bis zu fünfmal nachladen.
  const taskOnWorklist =
    !step?.taskId || (conversation?.worklist ?? []).some((task: any) => String(task?._id) === step.taskId);
  const worklistTries = useRef<{ taskId: string | null; count: number }>({ taskId: null, count: 0 });
  const [waitingForTask, setWaitingForTask] = useState(false);
  useEffect(() => {
    if (!conversation || !step?.taskId || taskOnWorklist) {
      setWaitingForTask(false);
      return;
    }
    if (worklistTries.current.taskId !== step.taskId) worklistTries.current = { taskId: step.taskId, count: 0 };
    if (worklistTries.current.count >= WORKLIST_RETRIES) {
      setWaitingForTask(false);
      return;
    }
    setWaitingForTask(true);
    const timer = window.setTimeout(() => {
      worklistTries.current.count += 1;
      loadConversation();
    }, WORKLIST_RETRY_MS);
    return () => window.clearTimeout(timer);
  }, [conversation, step?.taskId, taskOnWorklist, loadConversation]);

  /** The conversation with the task attached the way the chat page does it (`activeTask`). */
  const conversationWithTask = useMemo(() => {
    if (!conversation || !worklistTask) return conversation;
    return {
      ...conversation,
      activeTask: {
        ...worklistTask,
        ...(step?.userPrompt ? { userPrompt: step.userPrompt } : {}),
        triAttrs: {
          ...(worklistTask.triAttrs ?? {}),
          ...(step?.uxElement ? { uxElement: step.uxElement } : {}),
          ...(step?.userPrompt ? { userPrompt: step.userPrompt } : {}),
        },
      },
    };
  }, [conversation, worklistTask, step?.userPrompt, step?.uxElement]);

  if (!row) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex justify-end border-b border-border p-3">
          <Button variant="ghost" size="sm" className="gap-1" onClick={onClose}>
            <X size={14} aria-hidden /> {t("tenderBoard.panel.close")}
          </Button>
        </div>
        <SheetTitle className="sr-only">{t("tenderBoard.panel.title")}</SheetTitle>
        <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
          {panelError ? (
            panelError
          ) : (
            <>
              <Loader2 size={16} className="animate-spin" aria-hidden /> {t("tenderBoard.loading")}
            </>
          )}
        </div>
      </div>
    );
  }

  const mode: AnswerMode = pendingAnswer ? "none" : answerModeOf(row);
  const triage = row.status.key === "triage";
  const canAnswerHere = mode === "triage-start" || (["slider", "confirmation", "text"].includes(mode) && !!worklistTask);
  const question =
    mode === "triage-start" ? t("tenderBoard.panel.task.triageQuestion") : (step?.userPrompt ?? step?.name ?? null);
  // Knopftexte aus der BPMN (`tri:yesLabel` …) — nur die Beschriftung, gesendet wird wie im Chat.
  const yesLabel = step?.labels?.yes ?? t(triage ? "tenderBoard.panel.task.yesContinue" : "tenderBoard.panel.task.yes");
  const noLabel = step?.labels?.no ?? t("tenderBoard.panel.task.no");
  const confirmLabel = step?.labels?.confirm ?? t("tenderBoard.panel.task.confirm");
  const taskContext = mode !== "none" ? (step?.context ?? null) : null;
  const roleName = row.responsibleRoleName ?? row.currentStep?.role ?? t("tenderBoard.panel.task.someoneElse");
  /** Hat der Angemeldete die Rolle der Aufgabe? „unknown", wenn Aufgabe oder Anmeldung keine Rolle nennen. */
  const myRoleIds = new Set<string>(
    (authentication?.roleAssignments ?? [])
      .map((assignment: any) => (typeof assignment?.role === "string" ? assignment.role : assignment?.role?._id))
      .filter(Boolean)
      .map(String)
  );
  const roleOfStep: "held" | "missing" | "unknown" =
    !step?.roleId || myRoleIds.size === 0 ? "unknown" : myRoleIds.has(step.roleId) ? "held" : "missing";
  const owner = row.ownerUserId ?? row.currentStep?.assigneeUserId ?? null;
  const ownerName = owner ? (people[owner] ?? owner.replace(/@.*$/, "")) : null;
  const isMine = !!owner && !!currentUser && owner.toLowerCase() === currentUser.toLowerCase();

  /** The bot message the question is about — the last one before the task, not the task's own line. */
  const excerpt = (() => {
    const messages: any[] = conversation?.messages ?? [];
    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i];
      if (message?.type !== "botMessage" || typeof message?.content !== "string") continue;
      const text = plainExcerpt(taskExcerptSource(message.content));
      if (!text || (question && text === plainExcerpt(question))) continue;
      return { text, at: message.date ? String(message.date) : null };
    }
    return null;
  })();

  const runAction = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setActionError(null);
    try {
      await action();
      await Promise.all([refresh(), loadActivity()]);
    } catch (err: any) {
      if (mounted.current) setActionError(errorText(err));
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  /** The same request the chat sends for this task — same words, so the process compares the same. */
  const complete = async (input: string, label: string) => {
    if (!conversationId || !worklistTask) return;
    setBusy(true);
    setActionError(null);
    const answeredTaskId = String(step?.taskId ?? worklistTask._id);
    arrived.current = null;
    setAnswered({ taskId: answeredTaskId, label });
    setAnswerNote(t("tenderBoard.panel.task.sending", { answer: label }));
    const activation = [...(conversation?.messages ?? [])].reverse().find((message: any) => message?._taskActivation);
    try {
      const result = await userAPI.completeConversationTask(
        authentication.customer,
        conversationId,
        String(worklistTask._id),
        input,
        activation ? { content: activation.content, confirmation: activation.confirmation, resources: activation.resources } : undefined
      );
      if (!mounted.current) return;
      if (result) setConversation(result);
      setFreeText("");
      if (draftTaskId) writeDraft(draftTaskId, "");
      setAnswerNote(t("tenderBoard.panel.task.sent", { answer: label }));
    } catch (err: any) {
      if (!mounted.current) return;
      if (err instanceof TaskCompletionInterruptedError) {
        // The connection dropped, the server carries on — say so instead of reporting a failure.
        if (arrived.current !== answeredTaskId) setAnswerNote(t("tenderBoard.panel.task.interrupted", { answer: label }));
      } else {
        setAnswered(null);
        setAnswerNote(null);
        setActionError(errorText(err));
      }
    } finally {
      if (mounted.current) {
        setBusy(false);
        await Promise.all([refresh(), loadActivity(), loadConversation()]);
      }
    }
  };

  const startProcess = () => {
    if (definitions.length === 1) {
      runAction(() => api.startTenderProcess(row.tenderId, definitions[0].key, row.responsibleRoleId));
    } else {
      onOpenDialog({ kind: "startProcess", rows: [row] });
    }
  };

  const answers = (
    <div className="space-y-3">
      {mode === "triage-start" && (
        <div className="flex flex-wrap gap-2">
          <Button className="gap-1.5" disabled={busy || definitions.length === 0} onClick={startProcess}>
            <Check size={14} aria-hidden /> {t("tenderBoard.panel.task.yesContinue")}
          </Button>
          <Button variant="outline" className="gap-1.5" disabled={busy} onClick={() => onOpenDialog({ kind: "dismiss", rows: [row] })}>
            <X size={14} aria-hidden /> {t("tenderBoard.panel.task.noDismiss")}
          </Button>
          {definitions.length === 0 && (
            <span className="self-center text-xs text-muted-foreground">{t("tenderBoard.actions.noWorkflows")}</span>
          )}
          {definitions.length === 1 && (
            <span className="self-center text-xs text-muted-foreground">
              {t("tenderBoard.dialog.startWith", { workflow: definitions[0].text })}
            </span>
          )}
        </div>
      )}
      {mode === "slider" && (
        <div className="flex flex-wrap gap-2">
          <Button className="gap-1.5" disabled={busy || !worklistTask} onClick={() => complete("Ja", yesLabel)}>
            <Check size={14} aria-hidden /> {yesLabel}
          </Button>
          {/* Only „Nein“, also in triage: with a running process the answer goes to the process, which
              decides what follows — „verwerfen“ is the dismiss dialog, offered only without a process
              (decision 19.10.2026). */}
          <Button variant="outline" className="gap-1.5" disabled={busy || !worklistTask} onClick={() => complete("Nein", noLabel)}>
            <X size={14} aria-hidden /> {noLabel}
          </Button>
        </div>
      )}
      {mode === "confirmation" && (
        <Button className="gap-1.5" disabled={busy || !worklistTask} onClick={() => complete("Bestätigt", confirmLabel)}>
          <Check size={14} aria-hidden /> {confirmLabel}
        </Button>
      )}
      {mode === "text" && (
        <div className="space-y-2">
          {/* „Passt so“ (`tri:confirmLabel`) sendet genau diesen Text — das Gateway vergleicht ihn. */}
          {step?.labels?.confirm && (
            <Button className="gap-1.5" disabled={busy || !worklistTask} onClick={() => complete(step.labels!.confirm!, step.labels!.confirm!)}>
              <Check size={14} aria-hidden /> {step.labels.confirm}
            </Button>
          )}
          {step?.labels?.confirm && (
            <label className="block pt-1 text-xs font-medium text-muted-foreground" htmlFor="tender-panel-correction">
              {t("tenderBoard.panel.task.correctionLabel")}
            </label>
          )}
          <textarea
            id="tender-panel-correction"
            className="min-h-[72px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:border-ring"
            placeholder={t(step?.labels?.confirm ? "tenderBoard.panel.task.correctionPlaceholder" : "tenderBoard.panel.task.answerPlaceholder")}
            aria-label={t(step?.labels?.confirm ? "tenderBoard.panel.task.correctionLabel" : "tenderBoard.panel.task.answerPlaceholder")}
            value={freeText}
            disabled={busy || !worklistTask}
            onChange={(event) => changeFreeText(event.target.value)}
          />
          <Button
            variant={step?.labels?.confirm ? "outline" : "default"}
            disabled={busy || !worklistTask || !freeText.trim()}
            onClick={() => complete(freeText.trim(), freeText.trim())}
          >
            {t(step?.labels?.confirm ? "tenderBoard.panel.task.sendCorrection" : "tenderBoard.panel.task.send")}
          </Button>
        </div>
      )}
      {mode === "chat" && (
        <Button variant="outline" className="gap-1.5" onClick={() => setTab("chat")}>
          {t("tenderBoard.panel.task.answerInChat")} <ArrowRight size={14} aria-hidden />
        </Button>
      )}
      {/* Ohne Unterlagen bittet der Assistent um einen Upload — der Weg dorthin gehört neben die Frage,
          nicht nur in den Reiter (Prod 22.10.2026, Hanau-Duplikat ohne Unterlagen). */}
      {processLive && conversationId && row.documents.count === 0 && (
        <Button variant="outline" className="gap-1.5" onClick={() => setTab("documents")}>
          <Upload size={14} aria-hidden /> {t("tenderBoard.panel.task.uploadDocuments")}
        </Button>
      )}
      {["slider", "confirmation", "text"].includes(mode) && conversation && !worklistTask && !waitingForTask && (
        // Wer antworten darf, entscheidet die Rolle der Aufgabe, nicht „Übernehmen" (das setzt nur den
        // Owner). Der alte Satz ließ beides offen, und im Test galt „Übernehmen" als Pflicht (Prod 22.10.2026).
        <p className="text-xs text-muted-foreground">
          {roleOfStep === "missing" ? (
            t("tenderBoard.panel.task.notYourRole", { role: roleName })
          ) : roleOfStep === "held" ? (
            <>
              {t("tenderBoard.panel.task.notArrived")}{" "}
              <button
                type="button"
                className="underline underline-offset-2 hover:text-foreground"
                onClick={() => {
                  worklistTries.current = { taskId: step?.taskId ?? null, count: 0 };
                  loadConversation();
                }}
              >
                {t("tenderBoard.panel.task.checkAgain")}
              </button>
            </>
          ) : (
            t("tenderBoard.panel.task.notYours", { role: roleName })
          )}
        </p>
      )}
      {busy && answerNote && (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Loader2 size={12} className="animate-spin" aria-hidden /> {answerNote}
        </p>
      )}
      {!busy && answerNote && <p className="text-xs text-muted-foreground">{answerNote}</p>}
    </div>
  );

  const deadline = describeDeadline(row.deadline, new Date(), {
    today: (time) => (time ? t("tenderBoard.deadline.todayAt", { time }) : t("tenderBoard.deadline.today")),
    tomorrow: (time) => (time ? t("tenderBoard.deadline.tomorrowAt", { time }) : t("tenderBoard.deadline.tomorrow")),
    inDays: (days, time) =>
      time ? t("tenderBoard.deadline.inDaysAt", { days, time }) : t("tenderBoard.deadline.inDaysPlain", { days }),
    past: (days) => t("tenderBoard.deadline.past", { count: days }),
    pastToday: (time) => (time ? t("tenderBoard.deadline.pastTodayAt", { time }) : t("tenderBoard.deadline.pastToday")),
  });

  const statusBelow =
    row.triageState === "dismissed"
      ? null
      : question && (mode !== "none")
        ? t("tenderBoard.status.open", { task: question })
        : processLive
          ? run?.running
            ? t("tenderBoard.panel.status.working")
            : t("tenderBoard.status.automatic")
          : row.process
            ? t("tenderBoard.status.finished")
            : null;

  const docsCount = row.documents.count;
  const lastAction = row.lastAction;
  const lastByMe =
    !!lastAction?.actor && !!currentUser && lastAction.actor.toLowerCase() === currentUser.toLowerCase();

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Sticky Kopf */}
      <header className="shrink-0 border-b border-border bg-background px-5 pb-4 pt-3">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Button variant="ghost" size="sm" className="h-8 gap-1 px-2" disabled={!onPrevious} onClick={onPrevious}>
            <ChevronLeft size={14} aria-hidden /> {t("tenderBoard.panel.previous")}
          </Button>
          <Button variant="ghost" size="sm" className="h-8 gap-1 px-2" disabled={!onNext} onClick={onNext}>
            {t("tenderBoard.panel.next")} <ChevronRight size={14} aria-hidden />
          </Button>
          {position && (
            <span className="text-xs text-muted-foreground" data-testid="tender-panel-position">
              {t("tenderBoard.panel.position", { index: position.index + 1, total: position.total })}
            </span>
          )}
          <span className="flex-1" />
          <label className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
            <Checkbox checked={openInNewTab} onCheckedChange={(value) => onOpenInNewTabChange(value === true)} />
            {t("tenderBoard.panel.openInNewTab")}
          </label>
          <Button variant="outline" size="sm" className="h-8 gap-1" onClick={onClose}>
            <X size={14} aria-hidden /> {t("tenderBoard.panel.close")}
          </Button>
        </div>

        <SheetTitle className="mt-3 text-lg font-semibold leading-snug">{row.title}</SheetTitle>
        <SheetDescription asChild>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
            <span>{[row.buyer, row.place].filter(Boolean).join(" · ") || "—"}</span>
            <FitLight
              fit={row.fit}
              feedback={row.fitFeedback}
              people={people}
              onFeedback={(verdict) =>
                api.setTenderFitFeedback(row.tenderId, verdict).then(() => Promise.all([refresh(), loadActivity()]))
              }
            />
            <ExistingCustomerBadge
              row={row}
              people={people}
              onFeedback={(verdict, name) =>
                api.setExistingCustomerFeedback(row.tenderId, verdict, name).then(() => Promise.all([refresh(), loadActivity()]))
              }
            />
          </div>
        </SheetDescription>

        <div className="mt-4 grid grid-cols-3 gap-3">
          <div className="rounded-md border border-border p-3" data-testid="tender-panel-status">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("tenderBoard.columns.status")}</div>
            <span className="mt-1 inline-block rounded border border-border bg-muted px-2 py-0.5 text-xs font-semibold">
              {row.status.label}
            </span>
            {statusBelow && <div className="mt-1 text-xs text-muted-foreground">{statusBelow}</div>}
          </div>
          <div className="rounded-md border border-border p-3">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("tenderBoard.columns.owner")}</div>
            {ownerName ? (
              <div className="mt-1 flex items-center gap-2 text-sm">
                <span className="flex h-7 w-7 items-center justify-center rounded-full border border-border bg-muted text-[11px] font-bold">
                  {initialsOf(ownerName) || "?"}
                </span>
                <span className="truncate">{isMine ? t("tenderBoard.panel.you") : ownerName}</span>
              </div>
            ) : (
              <div className="mt-1 flex items-center gap-2 text-sm italic text-muted-foreground">
                <span className="flex h-7 w-7 items-center justify-center rounded-full border border-dashed border-muted-foreground/50 text-[11px] not-italic">
                  ?
                </span>
                {t("tenderBoard.owner.nobody")}
              </div>
            )}
          </div>
          <div
            className={`rounded-md border p-3 ${deadline?.highlight ? "border-foreground" : "border-border"}`}
            data-testid="tender-panel-deadline"
          >
            <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("tenderBoard.panel.deadline")}</div>
            {deadline ? (
              <>
                <div className={`mt-1 text-xl font-semibold leading-tight ${deadline.past ? "text-muted-foreground line-through" : ""}`}>
                  {deadline.relative}
                </div>
                <div className="text-xs text-muted-foreground">
                  {deadline.date}
                  {row.deadline && isDateOnly(row.deadline) ? ` · ${t("tenderBoard.dates.allDay")}` : ""}
                </div>
              </>
            ) : (
              <div className="mt-1 text-sm text-muted-foreground">—</div>
            )}
          </div>
        </div>

        {row.triageState !== "dismissed" && (
          <ol className="mt-3 flex flex-wrap items-center gap-x-1 gap-y-1 text-xs" aria-label={t("tenderBoard.panel.phaseBar.label")} data-testid="tender-panel-phasebar">
            {phaseBarSteps({ phase: row.phase, outcome: row.outcome, processLive }).map((phaseStep, index) => (
              <li key={phaseStep.key} className="flex items-center gap-1" data-state={phaseStep.state}>
                {index > 0 && <span className="text-muted-foreground" aria-hidden>›</span>}
                <span
                  className={
                    phaseStep.state === "current"
                      ? "rounded border border-foreground px-1.5 py-0.5 font-semibold"
                      : phaseStep.state === "done"
                        ? "px-1.5 py-0.5 text-muted-foreground"
                        : phaseStep.state === "skipped"
                          ? "px-1.5 py-0.5 text-muted-foreground/60 line-through"
                          : "px-1.5 py-0.5 text-muted-foreground/80"
                  }
                  aria-current={phaseStep.state === "current" ? "step" : undefined}
                >
                  {phaseStep.state === "done" && "✓ "}
                  {phaseStep.key === "ergebnis" && row.phase === "ergebnis" && row.outcomeLabel
                    ? t("tenderBoard.panel.phaseBar.ergebnisWith", { outcome: row.outcomeLabel })
                    : t(`tenderBoard.panel.phaseBar.${phaseStep.key}`)}
                </span>
              </li>
            ))}
          </ol>
        )}
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {/* Offene Aufgabe — oder: gerade nichts zu tun */}
        <section className="border-b border-border px-5 py-4" data-testid="tender-panel-task">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="flex-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {question && mode !== "none"
                ? t("tenderBoard.panel.task.open", { phase: row.status.label.toUpperCase() })
                : t("tenderBoard.panel.task.nothing")}
            </h2>
            {!isMine && (
              <Button
                size="sm"
                variant="outline"
                className="h-7"
                disabled={busy || !currentUser}
                onClick={() => runAction(() => api.setTenderOwner(row.tenderId, currentUser))}
              >
                {t("tenderBoard.actions.take")}
              </Button>
            )}
            <Button size="sm" variant="outline" className="h-7" disabled={busy} onClick={() => onOpenDialog({ kind: "handover", rows: [row] })}>
              {t("tenderBoard.panel.task.handover")}
            </Button>
            {conversationId && (
              <Button size="sm" variant="ghost" className="h-7 gap-1" onClick={() => setTab("chat")}>
                {t("tenderBoard.panel.task.toConversation")} <ArrowRight size={12} aria-hidden />
              </Button>
            )}
          </div>

          {question && mode !== "none" ? (
            <>
              <p className="mt-2 text-base font-semibold">{question}</p>
              {excerpt && (
                <figure className="mt-3 border-l-2 border-border pl-3">
                  <figcaption className="text-[11px] text-muted-foreground">
                    {t("tenderBoard.panel.task.excerptBy", {
                      when: excerpt.at ? relativeTime(excerpt.at, new Date()) : "",
                    })}
                  </figcaption>
                  <blockquote className="mt-1 text-sm text-foreground/90">„{excerpt.text}“</blockquote>
                  <button
                    type="button"
                    className="mt-1 inline-flex items-center gap-1 text-xs font-medium underline underline-offset-2"
                    onClick={() => {
                      setTab("chat");
                      setHighlightQuestion((value) => value + 1);
                    }}
                  >
                    {t("tenderBoard.panel.task.viewInChat")} <ArrowRight size={12} aria-hidden />
                  </button>
                </figure>
              )}
              {taskContext === "bid-decision" && (
                <div className="mt-4 rounded-md border border-border bg-muted/40 p-3 text-sm" data-testid="tender-panel-bid-basis">
                  <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {t("tenderBoard.panel.bid.basis")}
                  </div>
                  <dl className="grid grid-cols-[8rem_1fr] gap-x-3 gap-y-1">
                    {facts?.leistung && (
                      <>
                        <dt className="text-muted-foreground">{t("tenderBoard.panel.facts.leistung")}</dt>
                        <dd>{facts.leistung.length > 220 ? `${facts.leistung.slice(0, 220)} …` : facts.leistung}</dd>
                      </>
                    )}
                    {facts?.laufzeit && (
                      <>
                        <dt className="text-muted-foreground">{t("tenderBoard.panel.facts.laufzeit")}</dt>
                        <dd>{facts.laufzeit}</dd>
                      </>
                    )}
                    <dt className="text-muted-foreground">{t("tenderBoard.panel.deadline")}</dt>
                    <dd>{deadline ? `${deadline.date} · ${deadline.relative}` : "—"}</dd>
                  </dl>
                  <p className="mt-2 text-xs text-muted-foreground">{t("tenderBoard.panel.bid.next")}</p>
                </div>
              )}
              {taskContext === "concept-outline" && concept?.outline && (
                <div className="mt-4">
                  <TenderOutlineReview outline={concept.outline} />
                </div>
              )}
              {taskContext === "concept-document" && concept?.document && (
                <div className="mt-4 space-y-2">
                  <ConceptDocumentActions document={concept.document} />
                  <button type="button" className="text-xs font-medium underline underline-offset-2" onClick={() => setTab("concept")}>
                    {t("tenderBoard.panel.concept.toTab")}
                  </button>
                </div>
              )}
              <div className="mt-4">{answers}</div>
            </>
          ) : (
            <div className="mt-2 space-y-2 text-sm">
              {row.triageState === "dismissed" ? (
                <div className="flex flex-wrap items-center gap-2">
                  <span>{t("tenderBoard.panel.task.dismissed")}</span>
                  <Button size="sm" variant="outline" disabled={busy} onClick={() => runAction(() => api.restoreTender(row.tenderId))}>
                    {t("tenderBoard.actions.restore")}
                  </Button>
                </div>
              ) : processLive ? (
                <>
                  <div className="flex items-center gap-2 font-medium">
                    <Loader2 size={14} className="animate-spin" aria-hidden />
                    {run?.running || pendingAnswer ? t("tenderBoard.panel.status.working") : t("tenderBoard.status.automatic")}
                  </div>
                  {pendingAnswer && answered && (
                    <p className="text-muted-foreground" data-testid="tender-panel-answered">
                      {t("tenderBoard.panel.task.answeredWorking", { answer: answered.label })}
                    </p>
                  )}
                  {lastAction?.label && lastAction.eventType === "TASK_COMPLETED" && (
                    <p className="text-muted-foreground">
                      {lastByMe
                        ? t("tenderBoard.panel.task.lastByYou", { task: lastAction.label })
                        : t("tenderBoard.panel.task.lastBy", {
                            task: lastAction.label,
                            name: lastAction.actorName ?? (lastAction.actor ? lastAction.actor.replace(/@.*$/, "") : "TRI"),
                          })}
                    </p>
                  )}
                  <p className="text-muted-foreground">{t("tenderBoard.panel.task.nextAppearsHere")}</p>
                </>
              ) : (
                <p className="text-muted-foreground">{t("tenderBoard.status.finished")}</p>
              )}
            </div>
          )}
          {actionError && (
            <div className="mt-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">{actionError}</div>
          )}
        </section>

        <Tabs value={tab} onValueChange={(value) => setTab(value as PanelTab)} className="px-5 pb-8 pt-2">
          <TabsList className="sticky top-0 z-10 bg-background">
            <TabsTrigger value="overview">{t("tenderBoard.panel.tabs.overview")}</TabsTrigger>
            <TabsTrigger value="documents" className="gap-1.5">
              {t("tenderBoard.panel.tabs.documents")}
              {docsCount === 0 ? (
                <span className="rounded bg-amber-100 px-1 text-[10px] font-semibold text-amber-900">
                  {t("tenderBoard.panel.tabs.missing")}
                </span>
              ) : docsCount ? (
                <span className="text-xs text-muted-foreground">{docsCount}</span>
              ) : null}
            </TabsTrigger>
            <TabsTrigger value="concept" className="gap-1.5" data-testid="tender-panel-concept-tab">
              {t("tenderBoard.panel.tabs.concept")}
              {concept?.document ? (
                <span className="rounded bg-yellow-100 px-1 text-[10px] font-semibold text-yellow-900">
                  {t("tenderBoard.panel.concept.badgeOpen", { count: concept.document.openPlaceholders })}
                </span>
              ) : concept?.outline ? (
                <span className="rounded bg-amber-100 px-1 text-[10px] font-semibold text-amber-900">
                  {t("tenderBoard.panel.concept.badgeOutline")}
                </span>
              ) : null}
            </TabsTrigger>
            <TabsTrigger value="chat" className="gap-1.5" disabled={!conversationId}>
              {t("tenderBoard.panel.tabs.chat")}
              {run?.running && (
                <span
                  className="h-2 w-2 rounded-full bg-emerald-500"
                  aria-label={t("tenderBoard.panel.status.working")}
                  title={t("tenderBoard.panel.status.working")}
                />
              )}
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-1.5">
              {t("tenderBoard.panel.tabs.history")}
              {entries?.length ? <span className="text-xs text-muted-foreground">{entries.length}</span> : null}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="pt-4">
            <TenderPanelOverview
              row={row}
              facts={facts}
              location={location}
              locationLoading={location === undefined}
              people={people}
              busy={busy}
              onAddDate={(date) => api.addTenderDate(row.tenderId, date).then(() => Promise.all([refresh(), loadActivity()]))}
              onRemoveDate={(key) => api.removeTenderDate(row.tenderId, key).then(() => Promise.all([refresh(), loadActivity()]))}
              onReviewConflict={() => setTab("documents")}
            />
          </TabsContent>
          <TabsContent value="documents" className="pt-4">
            <TenderPanelDocuments row={row} facts={facts} onChanged={() => Promise.all([refresh(), loadActivity()])} />
          </TabsContent>
          <TabsContent value="concept" className="pt-4">
            <TenderPanelConceptTab concept={concept} />
          </TabsContent>
          <TabsContent value="chat" className="pt-4">
            {conversationId && (
              <TenderPanelChat
                conversationId={conversationId}
                conversation={conversationWithTask}
                error={conversationError}
                answerableHere={canAnswerHere && mode !== "triage-start"}
                question={mode === "triage-start" ? null : question}
                answers={answers}
                highlight={highlightQuestion}
                run={run}
                onConversationChange={setConversation}
                onAnswered={() => {
                  refresh();
                  loadActivity();
                  loadConversation();
                }}
              />
            )}
          </TabsContent>
          <TabsContent value="history" className="pt-4">
            <TenderPanelHistory entries={entries} loading={entriesLoading} currentUser={currentUser} people={people} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
